import assertLogger from "@/app/eventlogging/assertLogger";
import type { Maybe } from "@/components/utils/typeHelpers";

/**
 Open source implementation taken from: https://github.com/NicolasDeveloper/guid-typescript

 Copyright 2019 NicolasDeveloper

 Permission to use, copy, modify, and/or distribute this software for any purpose with or without fee is hereby granted,
 provided that the above copyright notice and this permission notice appear in all copies.

 THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS SOFTWARE INCLUDING ALL
 IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
 INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE
 OF THIS SOFTWARE.
 */
export default class Guid {
  public static validator = new RegExp(
    "^[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}$",
    "i"
  );

  // Only enforce that a guid is present
  public static containingValidator = new RegExp(
    "[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}",
    "i"
  );

  public static EMPTY = "00000000-0000-0000-0000-000000000000";

  public static isGuid(guid: any): guid is Guid {
    return (
      guid && (guid instanceof Guid || Guid.validator.test(guid.toString()))
    );
  }

  public static create(): Guid {
    return new Guid(
      [Guid.gen(2), Guid.gen(1), Guid.gen(1), Guid.gen(1), Guid.gen(3)].join(
        "-"
      )
    );
  }

  public static createEmpty(): Guid {
    return new Guid("emptyguid");
  }

  public static parseOrEmpty(text: string | null | undefined): Guid {
    return Guid.tryParse(text) ?? Guid.createEmpty();
  }

  public static parseOrThrow(
    text: string | null | undefined,
    errorText = "Invalid Guid"
  ): Guid {
    const result = Guid.tryParse(text);
    if (!result) {
      throw new Error(errorText);
    }
    return result;
  }

  public static canParse(text: string | null | undefined): text is string {
    return !!text && Guid.validator.test(text);
  }

  public static tryParse(text: string | null | undefined): Guid | null {
    return Guid.canParse(text) ? new Guid(text) : null;
  }

  public static raw(): string {
    return [
      Guid.gen(2),
      Guid.gen(1),
      Guid.gen(1),
      Guid.gen(1),
      Guid.gen(3),
    ].join("-");
  }

  private static gen(count: number): string {
    let out = "";
    for (let i = 0; i < count; i++) {
      out += Math.trunc((1 + Math.random()) * 0x1_00_00)
        .toString(16)
        .slice(1);
    }
    return out;
  }

  public readonly value: string;

  private constructor(text: string) {
    if (!text) {
      throw new TypeError("Invalid argument; `value` has no value.");
    }

    if (typeof text !== "string") {
      assertLogger.logViolation(
        "Guid Constructor called with non-string",
        "text.toString(): " + (text as any).toString()
      );
    }

    this.value = Guid.EMPTY;

    if (Guid.canParse(text)) {
      this.value = text;
    }
  }

  public equals(other: Maybe<Guid>): boolean {
    // Comparing string `value` against provided `guid` will auto-call
    // toString on `guid` for comparison

    // There seem to be several bugs in existing code where equals is called with other being null or undefined
    // removing the non-null check of other will break several e2e tests and probably other behaviour too.
    return !!other && this.value === other.toString();
  }

  public isEmpty(): boolean {
    return this.value === Guid.EMPTY;
  }

  public toString(): string {
    return this.value;
  }

  public toJSON(): any {
    return {
      value: this.value,
    };
  }

  public orNullIfEmpty(): Guid | null {
    return this.isEmpty() ? null : this;
  }
}

/**
 * Returns true if both guids are equally the same value or equally not values.
 */
export function optionalGuidsEquals(
  first: Guid | null | undefined,
  second: Guid | null | undefined
) {
  return (
    ((first === undefined || first === null) &&
      (second === undefined || second === null)) ||
    (first && second && first.equals(second))
  );
}
